import { ReactNode, useRef } from 'react'
import { Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'

interface ConfirmModalProps {
  modalRef: React.RefObject<HTMLDialogElement>
  title: string
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Color of the confirm button */
  color?: 'primary' | 'accent' | 'error'
  icon?: ReactNode
  isLoading?: boolean
  onConfirm: () => void
  onCancel?: () => void
}

export function useConfirmModal() {
  const modalRef = useRef<HTMLDialogElement>(null)
  return {
    modalRef,
    show: () => modalRef.current?.showModal(),
    close: () => modalRef.current?.close(),
  }
}

/**
 * Generic yes/no dialog for non-destructive confirmations.
 * For deletions use `DeleteConfirmModal` (error styling + trash icon).
 */
export function ConfirmModal({
  modalRef,
  title,
  message,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  color = 'primary',
  icon,
  isLoading,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  function handleCancel() {
    modalRef.current?.close()
    onCancel?.()
  }

  return (
    <Modal ref={modalRef} className="bg-neutral">
      <Modal.Header>
        <div className="flex flex-col items-center gap-3 pt-2">
          {icon && (
            <div className="bg-primary bg-opacity-20 text-primary rounded-full p-4">
              {icon}
            </div>
          )}
          <span className="text-xl font-bold text-center">{title}</span>
        </div>
        <button type="button" className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5"
          onClick={handleCancel}>✕</button>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4 items-center">
          <p className="text-center opacity-80">{message}</p>
          <div className="flex flex-row gap-3 justify-center">
            <Button color="ghost" disabled={isLoading} onClick={handleCancel}>{cancelLabel}</Button>
            <Button color={color} onClick={onConfirm} loading={isLoading} disabled={isLoading}>
              {confirmLabel}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
